<template>
	<a-modal
		class="g-pay-image-wall-edit-modal"
		:visible="visible"
		title="图片列表付费编辑"
		width="730px"
		:centered="true"
		:mask-closable="false"
		:destroy-on-close="true"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
		<a-form layout="vertical" class="g-pay-image-wall-edit-form">
			<div class="g-pay-image-wall-edit-form__row">
				<a-form-item label="标题" class="g-pay-image-wall-edit-form__col">
					<a-input v-model:value="form.title" placeholder="例如 唯美风景壁纸合集" />
				</a-form-item>
				<a-form-item label="副标题" class="g-pay-image-wall-edit-form__col">
					<a-input v-model:value="form.subtitle" placeholder="例如 共 9 张精选高清壁纸" />
				</a-form-item>
			</div>
			<div class="g-pay-image-wall-edit-form__row">
				<a-form-item label="现价" class="g-pay-image-wall-edit-form__col">
					<a-input v-model:value="form.price" placeholder="例如 3.90" />
				</a-form-item>
				<a-form-item label="免费预览张数" class="g-pay-image-wall-edit-form__col">
					<a-input-number
						v-model:value="form.freePreviewCount"
						:min="0"
						:precision="0"
						style="width: 100%;"
					/>
				</a-form-item>
			</div>
			<div class="g-pay-image-wall-edit-form__row">
				<a-form-item label="按钮文字" class="g-pay-image-wall-edit-form__col">
					<a-input v-model:value="form.buttonText" placeholder="立即解锁" />
				</a-form-item>
				<a-form-item label="按钮颜色" class="g-pay-image-wall-edit-form__col">
					<div class="g-pay-image-wall-edit-form__colors">
						<div class="g-pay-image-wall-edit-form__color-item">
							<ColorPicker v-model="form.buttonBg"  :swatchWidth ="29" :swatchHeight="29"	 />
							<a-input v-model:value="form.buttonBg" />
						</div>
					</div>
				</a-form-item>
			</div>
		</a-form>

		<div class="g-pay-image-wall-edit" @click="clearSelection">
			<div class="g-pay-image-wall-edit__actions" @click.stop>
				<span v-if="form.items.length > 1" class="g-pay-image-wall-edit__hint">图片支持拖拽排序</span>
				<div class="g-pay-image-wall-edit__actions-btns">
					<button type="button" class="g-pay-image-wall-edit__batch-btn" @click="addBatch">添加一批</button>
					<button type="button" class="g-pay-image-wall-edit__batch-btn" @click="addOne">添加一格</button>
				</div>
			</div>

			<EditListSortGroup
				ref="editGrid"
				class="g-pay-image-wall-edit__grid"
				@dragover="onSortDragOver"
			>
				<div
					v-for="(item, index) in form.items"
					:key="item.id"
					:data-sort-id="item.id"
					class="g-pay-image-wall-edit__cell-wrap"
					:class="[{ 'is-active': selectedIndex === index }, getEditListSortItemClass(dragId, item.id, dragOverId)]"
					@click.stop
				>
					<div
						class="g-pay-image-wall-edit__cell"
						:class="{ 'is-selected': selectedIndex === index }"
						:draggable="form.items.length > 1"
						@click="selectItem(index)"
						@dragstart="onItemDragStart(item.id)"
						@dragend="onItemDragEnd"
					>
						<div class="g-pay-image-wall-edit__thumb" :style="previewThumbStyle">
							<UploadProgressPanel
								v-if="isUploading && selectedIndex === index"
								:tasks="uploadTasks"
								variant="item"
								hide-filename
							/>
							<template v-else>
								<img v-if="getImageUrl(item)" :src="getImageUrl(item)" alt="" />
								<i v-else class="mbm-iconfont mbm-tupian"></i>
							</template>
						</div>
					</div>

					<div
						v-if="selectedIndex === index"
						class="g-pay-image-wall-edit__panel g-pay-image-wall-edit__panel--arrow g-pay-image-wall-edit__panel--float"
						:class="getPanelAlignClass(index)"
						@click.stop
					>
						<div class="g-pay-image-wall-edit__toolbar">
							<button type="button" class="g-pay-image-wall-edit__action" @click="triggerImageSelect">换图</button>
							<button
								type="button"
								class="g-pay-image-wall-edit__action"
								:class="{ 'is-active': activePanel === 'radius' }"
								@click="togglePanel('radius')"
							>圆角</button>
							<button
								type="button"
								class="g-pay-image-wall-edit__action g-pay-image-wall-edit__action--danger-text"
								:disabled="form.items.length <= 1"
								@click="deleteItem(index)"
							>删除</button>
						</div>
						<div v-if="activePanel === 'radius'" class="g-pay-image-wall-edit__sub-row">
							<span>圆角 (px)</span>
							<a-input-number v-model:value="radiusDraft" :min="0" :max="100" :precision="0" />
							<button type="button" class="g-pay-image-wall-edit__sub-confirm" @click="applyRadius">确定</button>
						</div>
					</div>
				</div>
			</EditListSortGroup>
		</div>
		</div>

		<input
			ref="imageInput"
			type="file"
			accept="image/*"
			class="g-pay-image-wall-edit__file-input"
			@change="onImageSelect"
		/>

		<template #footer>
			<a-button @click="onCancel">取消</a-button>
			<a-button type="primary" :disabled="isUploading" @click="onConfirm">确定</a-button>
		</template>
	</a-modal>
</template>

<script>
	import {
		isLocalImageFile,
		MAX_LOCAL_IMAGE_SIZE,
		revokeLocalImageUrl,
	} from '../utils/localImage.js';
	import { normalizeHexColor } from '../utils/richText.js';
	import {
		createDefaultPayImageWallItem,
		createDefaultPayImageWallItems,
		getPayImageWallImageUrl,
		PAY_IMAGE_WALL_BATCH_SIZE,
		normalizePayImageWallData,
	} from '../utils/payImageWall.js';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import ColorPicker from './ColorPicker.vue';
	import {
		createUploadTasks,
		updateUploadTaskProgress,
		uploadHeaderMediaFiles,
	} from '../utils/uploadHeaderMedia.js';
	import { ensureEditFloatPanelVisible, getGridPanelAlignClass } from '../utils/editPanelAlign.js';
	import EditListSortGroup from './EditListSortGroup.vue';
	import {
		createEditListDragState,
		getEditListSortItemClass,
		handleEditListSortDragEnd,
		handleEditListSortDragOver,
		handleEditListSortDragStart,
		resolveSelectedIndexAfterListMove,
	} from '../utils/editListDragSort.js';

	export default {
		name: 'GPayImageWallEditModal',
		components: { EditListSortGroup, UploadProgressPanel, ColorPicker },
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			data: {
				type: Object,
				default: () => ({}),
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				form: normalizePayImageWallData({}),
				selectedIndex: null,
				activePanel: '',
				radiusDraft: 10,
				isUploading: false,
				uploadTasks: [],
				...createEditListDragState(),
			};
		},
		computed: {
			previewThumbStyle() {
				return {
					borderRadius: `${this.form.borderRadius}px`,
				};
			},
		},
		watch: {
			visible(val) {
				if (val) {
					this.selectedIndex = null;
					this.activePanel = '';
					this.syncForm();
				}
			},
			data: {
				deep: true,
				handler() {
					if (this.visible) {
						this.syncForm();
					}
				},
			},
		},
		methods: {
			getEditListSortItemClass,
			getPanelAlignClass(index) {
				return getGridPanelAlignClass(index, 3);
			},
			ensureActivePanelVisible() {
				ensureEditFloatPanelVisible(this, {
					gridRef: 'editGrid',
					wrapSelector: '.g-pay-image-wall-edit__cell-wrap',
					panelSelector: '.g-pay-image-wall-edit__panel--float',
				});
			},
			normalizeColor(color) {
				return normalizeHexColor(color || '#ff4d4f');
			},
			onColorInput(field, color) {
				this.form[field] = color;
			},
			getImageUrl(item) {
				return getPayImageWallImageUrl(item?.image);
			},
			syncForm() {
				this.form = normalizePayImageWallData(this.data);
				this.radiusDraft = this.form.borderRadius;
				if (this.selectedIndex != null && this.selectedIndex >= this.form.items.length) {
					this.selectedIndex = null;
				}
			},
			selectItem(index) {
				this.selectedIndex = index;
				this.ensureActivePanelVisible();
			},
			clearSelection() {
				this.selectedIndex = null;
				this.activePanel = '';
			},
			togglePanel(panel) {
				this.activePanel = this.activePanel === panel ? '' : panel;
				if (panel === 'radius') this.radiusDraft = this.form.borderRadius;
				if (this.activePanel) {
					this.ensureActivePanelVisible();
				}
			},
			addBatch() {
				this.form.items.push(...createDefaultPayImageWallItems(PAY_IMAGE_WALL_BATCH_SIZE));
				this.selectedIndex = this.form.items.length - PAY_IMAGE_WALL_BATCH_SIZE;
			},
			addOne() {
				this.form.items.push(createDefaultPayImageWallItem({}, this.form.items.length));
				this.selectedIndex = this.form.items.length - 1;
			},
			onItemDragStart(id) {
				handleEditListSortDragStart(this, id);
			},
			onSortDragOver(event) {
				handleEditListSortDragOver(event, this.$refs.editGrid, this);
			},
			onItemDragEnd() {
				handleEditListSortDragEnd(this, this.$refs.editGrid, this.form.items, this, (result) => {
					this.selectedIndex = resolveSelectedIndexAfterListMove(
						this.selectedIndex,
						result.fromIndex,
						result.toIndex,
					);
				});
			},
			deleteItem(index) {
				if (this.form.items.length <= 1) return;
				const removed = this.form.items[index];
				if (removed?.image?.local) {
					revokeLocalImageUrl(removed.image);
				}
				this.form.items.splice(index, 1);
				this.selectedIndex = null;
				this.activePanel = '';
			},
			triggerImageSelect() {
				this.$refs.imageInput?.click();
			},
			async onImageSelect(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file || this.selectedIndex == null) return;
				if (!isLocalImageFile(file)) {
					this.$message?.error?.('请选择图片文件');
					return;
				}
				if (file.size > MAX_LOCAL_IMAGE_SIZE) {
					this.$message?.error?.('图片大小超出限制');
					return;
				}
				this.isUploading = true;
				this.uploadTasks = createUploadTasks([file]);
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'image',
						files: [file],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					const current = this.form.items[this.selectedIndex];
					if (current?.image?.local) {
						revokeLocalImageUrl(current.image.previewUrl);
					}
					this.form.items[this.selectedIndex].image = item;
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTasks = [];
				}
			},
			applyRadius() {
				this.form.borderRadius = this.radiusDraft;
				this.activePanel = '';
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				this.$emit('confirm', normalizePayImageWallData({ ...this.form }));
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	@import '../styles/edit-float-panel.less';

	.edit-float-panel(g-pay-image-wall-edit__panel);
	.edit-float-toolbar(g-pay-image-wall-edit__toolbar);
	.edit-float-action(g-pay-image-wall-edit__action);

	.g-pay-image-wall-edit-form__row {
		display: flex;
		gap: 16px;
	}

	.g-pay-image-wall-edit-form__col {
		flex: 1;
		min-width: 0;
	}

	.g-pay-image-wall-edit-form__colors {
		display: flex;
		gap: 16px;
		flex-wrap: wrap;
	}

	.g-pay-image-wall-edit-form__color-item {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 13px;
		color: #666;
		width: 100%;
	}

	.g-pay-image-wall-edit-form__swatch {
		position: relative;
		width: 28px;
		height: 28px;
		border-radius: 4px;
		border: 1px solid #e8e8e8;
		overflow: hidden;
		cursor: pointer;
	}

	.g-pay-image-wall-edit-form__color-input {
		position: absolute;
		inset: 0;
		opacity: 0;
		cursor: pointer;
	}

	.g-pay-image-wall-edit {
		min-height: 280px;
		padding: 8px 0 12px;
		margin-top: 8px;
	}

	.g-pay-image-wall-edit__actions {
		.edit-list-sort-actions-row();
		margin-bottom: 16px;
	}

	.g-pay-image-wall-edit__actions-btns {
		.edit-list-sort-actions-btns();
	}

	.g-pay-image-wall-edit__batch-btn {
		border: none;
		background: transparent;
		padding: 0;
		font-size: 13px;
		color: #285af9;
		cursor: pointer;
	}

	.g-pay-image-wall-edit__grid {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 5px;
		overflow: visible; 
	}

	.g-pay-image-wall-edit__cell-wrap {
		position: relative;
		z-index: 0;
		overflow: visible;

		&.is-active {
			z-index: 2;
		}
	}

	.g-pay-image-wall-edit__hint {
		.edit-list-sort-hint-text();
	}

	.g-pay-image-wall-edit__cell {
		border: 2px dashed #fff;
		border-radius: 2px;
		cursor: grab;

		&:active {
			cursor: grabbing;
		}

		&.is-selected {
			border-color: #285af9;
		}
	}

	.g-pay-image-wall-edit__thumb {
		position: relative;
		width: 100%;
		aspect-ratio: 16 / 10;
		overflow: hidden;
		background: #f0f0f0;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
			display: block;
		}

		.mbm-tupian {
			font-size: 24px;
			color: #ccc;
		}
	}

	.g-pay-image-wall-edit__sub-row {
		display: flex;
		align-items: center;
		flex-wrap: nowrap;
		gap: 8px;
		padding: 8px 12px 10px;
		border-top: 1px solid #f0f0f0;
		font-size: 12px;
		color: #666;
		white-space: nowrap;
	}
	.g-pay-image-wall-edit__sub-confirm {
		flex-shrink: 0;
		height: 28px;
		padding: 0 12px;
		border: none;
		border-radius: 4px;
		background: #285af9;
		color: #fff;
		font-size: 12px;
		line-height: 28px;
		white-space: nowrap;
		cursor: pointer;

		&:hover {
			background: #3868ff;
		}
	}
	.g-pay-image-wall-edit__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-pay-image-wall-edit-modal {
		.edit-modal-body();
		.edit-modal-scroll();
	}
</style>
